import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import ebayItemConditionReducer from "@/store/settings/ebay/item-condition";
import discountSettingReducer from "@/store/settings/ebay/discount-setting";
import miraklItemConditionReducer from "@/store/settings/mirakl/item-condition";
import discountAllocation from "@/store/settings/ebay/discount-allocation";
import shippingSettings from "@/store/settings/shipping";
import userReducer from "@/store/user";
import articleListingReducer from "@/store/article-listing";
import offerDataReducer from "@/store/article-listing/offer-data";
import productDetailsReducer from "@/store/product-details";
import bulkUploadNavigationReducer from "@/store/bulk-upload/navigation";
import bulkUploadItemConditionReducer from "@/store/bulk-upload/item-condition";
import bulkUploadReducer from "@/store/bulk-upload";
import bulkUploadOfferDetails from "@/store/bulk-upload/offer-details";
import tableFilterReducer from "@/store/table-filter";
import orderFiltersReducer from "@/store/order-filters";
import orderStepsReducer from "@/store/order-steps";
import returnsFiltersReducer from "@/store/returns-filters";
import { APP_STORE_SLICES } from "@/constants/app";

// Persist configuration for user slice only
const userPersistConfig = {
  key: "user",
  storage,
  whitelist: [
    "accessToken",
    "refreshToken",
    "user",
    "securityGroups",
    "selectedSecurityGroup",
    "loginData",
    "previousSecurityGroup",
  ],
};

// Persist configuration for product details slice
const productDetailsPersistConfig = {
  key: "productDetails",
  storage,
  whitelist: ["currentView", "selectedGtinID"],
};

const articleListingPersistConfig = {
  key: APP_STORE_SLICES.EASY_LISTER.ARTICLE_LISTING,
  storage,
  whitelist: ["persistedConditionDescription"],
};

const bulkUploadOfferDetailConfig = {
  key: APP_STORE_SLICES.BULK_UPLOAD.OFFER_DETAILS,
  storage,
  whitelist: ["bulkUploadSelectedProcess"],
};

const orderInvoicesPersistConfig = {
  key: "orderInvoices",
  storage,
  whitelist: ["invoices"],
};

// Persist configuration for order steps slice
const orderStepsPersistConfig = {
  key: "orderSteps",
  storage,
  whitelist: ["steps", "stepData", "noClubNumberOrders"],
};

const persistedUserReducer = persistReducer(userPersistConfig, userReducer);

const persistedProductDetailsReducer = persistReducer(
  productDetailsPersistConfig,
  productDetailsReducer,
);

const persistedBulkUploadOfferDetailReducer = persistReducer(
  bulkUploadOfferDetailConfig,
  bulkUploadOfferDetails,
);

const persistedArticleListingReducer = persistReducer(
  articleListingPersistConfig,
  articleListingReducer,
);

// Create persisted reducer for order steps
const persistedOrderStepsReducer = persistReducer(
  orderStepsPersistConfig,
  orderStepsReducer,
);

export const store = configureStore({
  reducer: {
    // user authentication
    user: persistedUserReducer,

    // ebay settings for discount
    ebayItemCondition: ebayItemConditionReducer,
    discountSetting: discountSettingReducer,
    discountAllocation,
    // mirakl settings
    miraklItemCondition: miraklItemConditionReducer,

    // shipping settings
    shippingSettings,

    // article listing
    articleListing: persistedArticleListingReducer,
    offerData: offerDataReducer,

    // product details
    productDetails: persistedProductDetailsReducer,

    // bulk upload
    bulkUpload: bulkUploadReducer,
    bulkUploadNavigation: bulkUploadNavigationReducer,
    bulkUploadItemCondition: bulkUploadItemConditionReducer,
    bulkUploadOfferDetails: persistedBulkUploadOfferDetailReducer,
    tableFilter: tableFilterReducer,

    // order filters
    orderFilters: orderFiltersReducer,

    // order steps (persistent)
    orderSteps: persistedOrderStepsReducer,

    // returns filters
    returnsFilters: returnsFiltersReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }),
});

export const persistor = persistStore(store);
